import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './app/app.css';
import { Router } from './app/Router';
import { AuthProvider } from './core/auth';
import { AvisoProvider } from './ui/aviso';
import { ligarRevelacao } from './ui/revelar';
import { ThemeProvider } from './ui/theme';

/**
 * Sem `staleTime`, toda consulta nasce velha.
 *
 * O padrão do React Query é zero: qualquer tela que monte de novo dispara a
 * mesma consulta de novo, mesmo que a resposta tenha chegado há dois segundos.
 * Navegar entre as abas de um produto e voltar refazia tudo, e cada ida ao
 * Supabase é uma volta de rede inteira mais a checagem de RLS.
 *
 * Trinta segundos é curto o bastante para ninguém trabalhar em cima de dado
 * velho, e longo o bastante para cobrir o vaivém entre telas, que é onde a
 * lentidão aparecia. Quem precisa de dado fresco na hora continua invalidando
 * a chave depois de gravar, que é como o resto do código já faz.
 *
 * `gcTime` de cinco minutos mantém o que saiu da tela em memória: voltar para
 * a aba anterior passa a pintar na hora, com revalidação por trás.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
    },
  },
});

// A camada de movimento do design system. Fora do React de propósito: ela
// observa o documento inteiro, inclusive o que rotas preguiçosas montarem
// depois, e não pertence a nenhum componente em particular.
ligarRevelacao();

const el = document.getElementById('root');
if (!el) throw new Error('root não encontrado');

createRoot(el).render(
  <StrictMode>
    <BrowserRouter>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/*
            O toast (`AvisoProvider`) fica ACIMA do roteador de propósito: uma
            ação disparada de uma tela pode navegar antes do aviso sumir (ex.:
            criar e já ir para o editor), e o aviso precisa sobreviver à troca
            de rota até a sua própria contagem de tempo terminar.
          */}
          <AvisoProvider>
            {/*
              `v7_startTransition` embrulha a troca de rota numa transição.

              Sem ela, clicar num link cuja tela ainda não chegou fazia o React
              avisar: "a component suspended while responding to synchronous
              input", e a tela INTEIRA era substituída pelo indicador de
              carregamento. Com a transição, a tela atual fica de pé enquanto o
              pedaço novo chega, e a troca acontece quando ele está pronto.

              Só passou a importar depois que as telas viraram carregamento
              preguiçoso: antes nada suspendia, porque tudo já estava na memória.

              A flag `future={{ v7_startTransition: true }}` saiu na migração
              para o react-router v7 (01/09/2026): lá esse comportamento é o
              padrão, e a propriedade deixou de existir. O que ela ligava
              continua valendo - o motivo acima é por que ainda importa.
            */}
            <Router />
          </AvisoProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
    </BrowserRouter>
  </StrictMode>,
);
